import React, { useEffect, useMemo, useRef, useState } from 'react';

export default function ImageWithFallback({
  src,
  alt = '',
  fallbackSrc = null,
  className = '',
  fetchPriority = 'auto',
  maxRetries = 2,
  width,
  height,
}) {
  const [currentSrc, setCurrentSrc] = useState(src || fallbackSrc || '');
  const [status, setStatus] = useState('loading');
  const retryRef = useRef(0);

  useEffect(() => {
    setCurrentSrc(src || fallbackSrc || '');
    setStatus('loading');
    retryRef.current = 0;
  }, [src, fallbackSrc]);

  const retry = () => {
    if (retryRef.current >= maxRetries) {
      if (fallbackSrc && currentSrc !== fallbackSrc) {
        setCurrentSrc(fallbackSrc);
        setStatus('loading');
        return;
      }
      setStatus('error');
      return;
    }
    retryRef.current += 1;
    const bust = `cb=${Date.now()}`;
    const url = new URL(currentSrc || src, window.location.origin);
    url.searchParams.set('cb', bust);
    setCurrentSrc(url.toString());
    setStatus('loading');
  };

  const handleError = () => {
    if (fallbackSrc && currentSrc !== fallbackSrc) {
      setCurrentSrc(fallbackSrc);
      setStatus('loading');
    } else {
      retry();
    }
  };

  const handleLoad = () => setStatus('loaded');

  const style = useMemo(() => {
    if (width && height) return { aspectRatio: `${width} / ${height}` };
    return undefined;
  }, [width, height]);

  return (
    <div className={`custom_img-wrapper ${status}`} style={style}>
      {status !== 'loaded' && (
        <div className="custom_img-skeleton" aria-hidden="true" />
      )}
      <img
        src={currentSrc}
        alt={alt}
        className={className}
        decoding="async"
        loading="lazy"
        fetchpriority={fetchPriority}
        onLoad={handleLoad}
        onError={handleError}
      />
    </div>
  );
}
